/* import __COLOCATED_TEMPLATE__ from './filter-predicates.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { type default as PredicateGroup } from 'predicates/models/predicate-group';
import { type default as Predicate } from 'predicates/models/predicate';
import type Attribute from 'embercom/models/attribute';

interface Args {
  predicateGroup?: PredicateGroup;
  ticketFields?: Attribute[];
  onUpdatePredicates: (predicates: Predicate[]) => void;
}

interface Signature {
  Args: Args;
}

export default class FilterPredicates extends Component<Signature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked attributes: any[];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.attributes = this.buildAttributes();
  }

  private buildAttributes(): any[] {
    if (!this.args.ticketFields) {
      return [];
    }
    return this.args.ticketFields.map((field: Attribute) => this.buildAttribute(field));
  }

  get attributeGroupList() {
    let orderedAttributes = this.attributes.sort((a: any, b: any) => a.name.localeCompare(b.name));

    return [
      {
        attributes: orderedAttributes,
      },
    ];
  }

  get supportedPredicateAttributes(): string[] {
    return this.attributes.map((attribute: any) => attribute.identifier);
  }

  private buildAttribute(field: Attribute) {
    let options = this.buildOptions(field);
    let { options: _, ...fieldWithoutOptions } = field; // destructure to exclude options
    let attribute = this.store.createRecord('attribute', fieldWithoutOptions);

    attribute.options.pushObjects(options);

    return attribute;
  }

  private buildOptions(field: Attribute) {
    if (!this.isListType(field.type)) {
      return [];
    }

    let listOptions = field.options;
    if (!listOptions || listOptions.length === 0) {
      return [];
    }

    return listOptions.map((option: any) => {
      return this.store.createRecord('people/attribute-option', option);
    });
  }

  @action
  onUpdatePredicates(predicates: Predicate[]) {
    this.args.onUpdatePredicates?.(predicates);
  }

  private isListType(type: string): boolean {
    return [
      'picklist',
      'dropdown',
      'multiselect',
      'priority',
      'tickettype',
      'list',
      'conversation_attribute_list',
    ].includes(type);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ConversationIngestion::FilterPredicates': typeof FilterPredicates;
    'standalone/common/conversation-ingestion/filter-predicates': typeof FilterPredicates;
  }
}
